import type React from 'react';
import { Button, Input, Label, Modal, TextField } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';

type CredentialModalViewProps = {
    slot: string;
    title: string;
    description: string;
    label: string;
    placeholder: string;
    submitLabel: string;
    cancelLabel: string;
    inputType: 'text' | 'password';
    inputMode?: 'numeric' | 'text';
    error: string | null;
    isOpen: boolean;
    isSubmitting: boolean;
    value: string;
    isValid: boolean;
    onChange: (value: string) => void;
    onClose: () => void;
    onSubmit: () => void;
};

/**
 * Render-only single-field credential modal (Set-PIN / Reset-Password). Field
 * state lives in {@link useCredentialForm}; the container wires it in.
 */
export const CredentialModalView: React.FC<CredentialModalViewProps> = ({
    slot,
    title,
    description,
    label,
    placeholder,
    submitLabel,
    cancelLabel,
    inputType,
    inputMode,
    error,
    isOpen,
    isSubmitting,
    value,
    isValid,
    onChange,
    onClose,
    onSubmit,
}) => (
    <Modal isOpen={isOpen} onOpenChange={(open) => !open && onClose()}>
        <Modal.Backdrop>
            <Modal.Container size="sm">
                <Modal.Dialog {...getNodeTestIdProp(`${slot}-modal`)}>
                    <Modal.CloseTrigger />
                    <Modal.Header
                        {...getNodeTestIdProp(`${slot}-modal-header`)}
                    >
                        <Modal.Heading
                            {...getNodeTestIdProp(`${slot}-modal-title`)}
                        >
                            {title}
                        </Modal.Heading>
                        <p
                            className="text-sm text-muted"
                            {...getNodeTestIdProp(`${slot}-modal-description`)}
                        >
                            {description}
                        </p>
                    </Modal.Header>
                    <Modal.Body
                        className="flex flex-col gap-4"
                        {...getNodeTestIdProp(`${slot}-modal-body`)}
                    >
                        <TextField
                            fullWidth
                            isRequired
                            onChange={onChange}
                            type={inputType}
                            value={value}
                            {...getNodeTestIdProp(`${slot}-field`)}
                        >
                            <Label>{label}</Label>
                            <Input
                                inputMode={inputMode}
                                placeholder={placeholder}
                                type={inputType}
                            />
                        </TextField>
                        {error ? (
                            <p
                                className="text-sm text-danger"
                                role="alert"
                                {...getNodeTestIdProp(`${slot}-modal-error`)}
                            >
                                {error}
                            </p>
                        ) : null}
                    </Modal.Body>
                    <Modal.Footer
                        {...getNodeTestIdProp(`${slot}-modal-footer`)}
                    >
                        <Button
                            isDisabled={isSubmitting}
                            onPress={onClose}
                            variant="tertiary"
                            {...getNodeTestIdProp(`${slot}-modal-cancel`)}
                        >
                            {cancelLabel}
                        </Button>
                        <Button
                            isDisabled={!isValid}
                            isPending={isSubmitting}
                            onPress={onSubmit}
                            variant="primary"
                            {...getNodeTestIdProp(`${slot}-modal-submit`)}
                        >
                            {submitLabel}
                        </Button>
                    </Modal.Footer>
                </Modal.Dialog>
            </Modal.Container>
        </Modal.Backdrop>
    </Modal>
);
